import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import NewFactureDialog from '@/components/NewFactureDialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Receipt, Search, Loader2, Building, Calendar, Hash, ChevronRight,
  Plus, MoreVertical, Pencil, Copy, Trash2,
} from 'lucide-react';

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const STATUT_STYLE = {
  'Émise':           'bg-blue-100 text-blue-700',
  'Envoyée Chorus':  'bg-purple-100 text-purple-700',
  'Payée':           'bg-emerald-100 text-emerald-700',
  'Rejetée':         'bg-red-100 text-red-700',
};

// Sub-badge color for the live Chorus lifecycle status
const CHORUS_STATUS_STYLE = {
  DEPOSEE:                            'bg-purple-50 text-purple-700 border-purple-200',
  MISE_A_DISPOSITION:                 'bg-purple-50 text-purple-700 border-purple-200',
  MISE_A_DISPOSITION_DESTINATAIRE:    'bg-purple-50 text-purple-700 border-purple-200',
  SERVICE_FAIT:                       'bg-indigo-50 text-indigo-700 border-indigo-200',
  MANDATEE:                           'bg-indigo-50 text-indigo-700 border-indigo-200',
  COMPTABILISEE:                      'bg-emerald-50 text-emerald-700 border-emerald-200',
  MISE_EN_PAIEMENT:                   'bg-emerald-50 text-emerald-700 border-emerald-200',
  A_RECYCLER:                         'bg-amber-50 text-amber-700 border-amber-200',
  SUSPENDUE:                          'bg-amber-50 text-amber-700 border-amber-200',
  REJETEE:                            'bg-red-50 text-red-700 border-red-200',
};

const fmtChorus = (s) => (s || '').replaceAll('_', ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

export default function Factures() {
  const navigate = useNavigate();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get('/factures');
      setFactures(r.data || []);
    } catch (e) {
      toast.error("Impossible de charger les factures");
    }
    setLoading(false);
  };

  useEffect(() => {
    document.title = 'Factures - Reno Resolve';
    reload();
  }, []);

  const filtered = factures.filter((f) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (f.numero || '').toLowerCase().includes(q) ||
      (f.client_info?.nom || '').toLowerCase().includes(q) ||
      (f.numero_bon_commande || '').toLowerCase().includes(q) ||
      (f.numero_engagement || '').toLowerCase().includes(q) ||
      (f.chorus_id ? String(f.chorus_id) : '').toLowerCase().includes(q) ||
      (f.project_title || '').toLowerCase().includes(q)
    );
  });

  const totalTTC = factures.reduce((s, f) => s + (f.totaux?.total_ttc || 0), 0);
  const nbParStatut = factures.reduce((acc, f) => {
    acc[f.statut] = (acc[f.statut] || 0) + 1;
    return acc;
  }, {});

  const handleDuplicate = async (f, e) => {
    e?.stopPropagation();
    setBusyId(f.facture_id);
    try {
      const r = await api.post(`/factures/${f.facture_id}/duplicate`);
      toast.success(`Facture dupliquée : ${r.data.facture.numero}`);
      await reload();
      navigate(`/factures/${r.data.facture.facture_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la duplication");
    }
    setBusyId(null);
  };

  const handleDelete = async (f, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Supprimer la facture ${f.numero} ? Le devis source sera ré-ouvert.`)) return;
    setBusyId(f.facture_id);
    try {
      await api.delete(`/factures/${f.facture_id}`);
      toast.success("Facture supprimée");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la suppression");
    }
    setBusyId(null);
  };

  return (
    <div className="space-y-5" data-testid="factures-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Factures</h1>
            <p className="text-sm text-slate-500 mt-0.5">{factures.length} facture(s) · {fmt(totalTTC)} € TTC cumulé</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(nbParStatut).map(([s, n]) => (
            <Badge key={s} className={STATUT_STYLE[s] || 'bg-slate-100 text-slate-700'}>{s} · {n}</Badge>
          ))}
          <Button
            onClick={() => setShowNew(true)}
            className="bg-emerald-600 hover:bg-emerald-700 ml-2"
            data-testid="new-facture-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle facture
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (n°, client, BC, n° engagement, ID Chorus, projet)..."
          className="pl-9"
          data-testid="factures-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="factures-empty">
          <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            {search ? 'Aucune facture ne correspond' : 'Aucune facture pour le moment'}
          </p>
          {!search && (
            <Button onClick={() => setShowNew(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-1.5" /> Créer la première facture
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((f) => {
            const isLocked = ['Envoyée Chorus', 'Payée'].includes(f.statut);
            return (
              <div
                key={f.facture_id}
                className="bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all flex items-center gap-3 p-4"
                data-testid={`facture-row-${f.facture_id}`}
              >
                <button
                  onClick={() => navigate(`/factures/${f.facture_id}`)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Receipt className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-900">{f.numero}</p>
                      <Badge className={STATUT_STYLE[f.statut] || 'bg-slate-100 text-slate-700'}>{f.statut}</Badge>
                      {f.chorus_status && (
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${CHORUS_STATUS_STYLE[f.chorus_status] || 'bg-purple-50 text-purple-700 border-purple-200'}`}
                          data-testid={`facture-chorus-status-${f.facture_id}`}
                          title={`Statut Chorus : ${f.chorus_status}${f.chorus_id ? ' · n° ' + f.chorus_id : ''}`}
                        >
                          Chorus · {fmtChorus(f.chorus_status)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Building className="w-3 h-3" />{f.client_info?.nom || '—'}</span>
                      {f.numero_bon_commande && <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />BC {f.numero_bon_commande}</span>}
                      {f.numero_engagement && <span className="inline-flex items-center gap-1 text-indigo-600"><Hash className="w-3 h-3" />Eng. {f.numero_engagement}</span>}
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{f.date_facture || '—'}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-900">{fmt(f.totaux?.total_ttc || 0)} €</p>
                    <p className="text-xs text-slate-400">TTC</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>

                {/* Action menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg shrink-0"
                      data-testid={`facture-menu-${f.facture_id}`}
                      disabled={busyId === f.facture_id}
                    >
                      {busyId === f.facture_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <MoreVertical className="w-4 h-4" />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); navigate(`/factures/${f.facture_id}`); }}
                      data-testid={`facture-action-edit-${f.facture_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => handleDuplicate(f, e)}
                      data-testid={`facture-action-duplicate-${f.facture_id}`}
                    >
                      <Copy className="w-3.5 h-3.5 mr-2" /> Dupliquer
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => handleDelete(f, e)}
                      disabled={isLocked}
                      className="text-red-600 focus:text-red-700"
                      data-testid={`facture-action-delete-${f.facture_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      {/* Creation dialog */}
      <NewFactureDialog
        open={showNew}
        onOpenChange={setShowNew}
        onFactureCreated={reload}
      />
    </div>
  );
}
